import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useCourseListQuery } from "@/services/courses/use-course-list-query";
import type { CourseScope } from "@/services/courses/use-course-list-query";

import { liveCoursesApi } from "@/api";
import type {
  AddHomeworkData,
  CourseFilter,
  CreateCourseData,
  CreateSessionData,
  LiveSession,
  ScheduleSlotInput,
  UpdateLiveSettingsData,
  UpdateSessionData,
  UpsertGradingSchemeData,
} from "@/api";
import { QUERY_KEYS } from "@/constants";

/**
 * Jonli kurslar ro'yxati, rolga qarab:
 *  - "mine" → o'qituvchining o'z jonli kurslari (`/live-courses/my`)
 *  - "all"  → to'liq jonli katalog (`/live-courses`); admin hamma holatni oladi.
 */
export const useLiveCourseList = (filter: CourseFilter, scope: CourseScope, enabled = true) =>
  useCourseListQuery("live", filter, scope, enabled);

export const useCreateLiveCourse = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCourseData) => liveCoursesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_LIVE_COURSES] });
      toast.success(t("COMMON.ADDED"));
    },
  });
};

export const useSubmitLiveCourse = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => liveCoursesApi.submit(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_LIVE_COURSES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_LIVE_COURSES] });
      toast.success(t("COURSES.SUBMITTED"));
    },
  });
};

export const useDeleteLiveCourse = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => liveCoursesApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_LIVE_COURSES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_LIVE_COURSES] });
      toast.success(t("COMMON.DELETED"));
    },
  });
};

// ─── Jonli kurs sozlamalari ─────────────────────────────────────────────────

export const useUpdateLiveSettings = (courseId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateLiveSettingsData) => liveCoursesApi.updateSettings(courseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE, courseId] });
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

// ─── Haftalik jadval ──────────────────────────────────────────────────────────

export const useLiveSchedule = (courseId: number | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_LIVE_SCHEDULE, courseId],
    queryFn: () => liveCoursesApi.getSchedule(courseId as number),
    enabled: !!courseId,
    select: res => res.data,
  });
};

export const useReplaceSchedule = (courseId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slots: ScheduleSlotInput[]) => liveCoursesApi.replaceSchedule(courseId, slots),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_LIVE_SCHEDULE, courseId] });
    },
  });
};

// ─── Sessiyalar ───────────────────────────────────────────────────────────────

/**
 * Kursning jonli sessiyalari.
 *
 * `live` bir daqiqalik so'rovni yoqadi: sessiyaning holati server tomonda vaqt
 * bo'yicha o'zgaradi, shuning uchun dars ro'yxatida o'tirgan o'quvchi allaqachon
 * tugagan darsni "Jonli" deb — ustiga qo'shilish tugmasi bilan — sahifani
 * yangilash xayoliga kelguncha ko'rib turardi. O'qituvchining kurs quradigan
 * ekranlariga bu kerak emas.
 */
export const useLiveSessions = (courseId: number | undefined, options?: { live?: boolean }) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_LIVE_SESSIONS, courseId],
    queryFn: () => liveCoursesApi.getSessions(courseId as number),
    enabled: !!courseId,
    select: res => res.data,
    refetchInterval: options?.live ? 60_000 : false,
    refetchOnWindowFocus: options?.live ?? false,
  });
};

export const useGenerateSessions = (courseId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (replaceExisting: boolean) =>
      liveCoursesApi.generateSessions(courseId, replaceExisting),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_LIVE_SESSIONS, courseId] });
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

export const useCreateSession = (courseId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSessionData) => liveCoursesApi.createSession(courseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_LIVE_SESSIONS, courseId] });
      toast.success(t("COMMON.ADDED"));
    },
  });
};

export const useReorderSessions = (courseId: number) => {
  const queryClient = useQueryClient();
  const queryKey = [QUERY_KEYS.GET_LIVE_SESSIONS, courseId];

  return useMutation({
    mutationFn: (sessionIds: number[]) => liveCoursesApi.reorderSessions(courseId, sessionIds),
    onMutate: async (sessionIds: number[]) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<{ data: LiveSession[] }>(queryKey);
      if (previous?.data) {
        const byId = new Map(previous.data.map(s => [s.id, s]));
        const reordered = sessionIds
          .map(id => byId.get(id))
          .filter((s): s is LiveSession => Boolean(s));
        queryClient.setQueryData(queryKey, { ...previous, data: reordered });
      }
      return { previous };
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useUpdateSession = (courseId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateSessionData }) =>
      liveCoursesApi.updateSession(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_LIVE_SESSIONS, courseId] });
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

export const useDeleteSession = (courseId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => liveCoursesApi.removeSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_LIVE_SESSIONS, courseId] });
      toast.success(t("COMMON.DELETED"));
    },
  });
};

// ─── Darsni olib borish ─────────────────────────────────────────────────────
// Rejalashtiruvchi jadvalga o'zi amal qiladi; bular esa o'qituvchining qo'lda
// aralashuvi — erta boshlash, kech tugatish yoki darsni bekor qilish.

/** Sessiya holatiga bog'liq hammasini yangilaydi: ro'yxat, jurnal, davomat. */
const invalidateSessionViews = (
  queryClient: ReturnType<typeof useQueryClient>,
  courseId: number,
) => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_LIVE_SESSIONS, courseId] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE_JOURNAL, courseId] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_ATTENDANCE_SUMMARY, courseId] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_SESSION_ROSTER] });
};

export const useStartSession = (courseId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: number) => liveCoursesApi.startSession(sessionId),
    onSuccess: res => {
      invalidateSessionViews(queryClient, courseId);
      if (res.message) toast.success(res.message);
    },
  });
};

export const useEndSession = (courseId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: number) => liveCoursesApi.endSession(sessionId),
    onSuccess: res => {
      invalidateSessionViews(queryClient, courseId);
      if (res.message) toast.success(res.message);
    },
  });
};

export const useCancelSession = (courseId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, reason }: { sessionId: number; reason?: string }) =>
      liveCoursesApi.cancelSession(sessionId, reason),
    onSuccess: res => {
      invalidateSessionViews(queryClient, courseId);
      if (res.message) toast.success(res.message);
    },
  });
};

/**
 * Sessiya xonasini ochadi. O'quvchi uchun so'rovning o'zi uning kelganini qayd
 * etadi, shuning uchun chiqishda o'quvchining o'z jurnali ham yangilanadi.
 */
export const useJoinSession = (courseId?: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: number) => liveCoursesApi.joinSession(sessionId),
    onSuccess: () => {
      if (!courseId) return;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_JOURNAL, courseId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_ATTENDANCE, courseId] });
    },
  });
};

// ─── Uy vazifasi (har sessiya uchun) ────────────────────────────────────────

export const useSessionHomework = (sessionId: number | undefined, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_SESSION_HOMEWORK, sessionId],
    queryFn: () => liveCoursesApi.getSessionHomework(sessionId as number),
    enabled: !!sessionId && enabled,
    select: res => res.data,
  });
};

export const useAddHomework = (sessionId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddHomeworkData) => liveCoursesApi.addHomework(sessionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_SESSION_HOMEWORK, sessionId] });
      toast.success(t("COMMON.ADDED"));
    },
  });
};

// ─── Baholash sxemasi ───────────────────────────────────────────────────────

export const useGradingScheme = (courseId: number | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_LIVE_GRADING, courseId],
    queryFn: () => liveCoursesApi.getGradingScheme(courseId as number),
    enabled: !!courseId,
    retry: false,
    select: res => res.data,
  });
};

export const useUpsertGradingScheme = (courseId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpsertGradingSchemeData) =>
      liveCoursesApi.upsertGradingScheme(courseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_LIVE_GRADING, courseId] });
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

// ─── Kurs hayoti ──────────────────────────────────────────────────────────────

/** Yakunlashdan oldingi raqamlar — oyna har ochilganda yangidan. */
export const useFinishPreview = (courseId: number, enabled: boolean) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_FINISH_PREVIEW, courseId],
    queryFn: () => liveCoursesApi.getFinishPreview(courseId),
    enabled,
    staleTime: 0,
    select: res => res.data,
  });

const invalidateLifecycle = (queryClient: ReturnType<typeof useQueryClient>, courseId: number) => {
  for (const key of [
    QUERY_KEYS.GET_COURSE,
    QUERY_KEYS.GET_LIVE_SESSIONS,
    QUERY_KEYS.GET_COURSE_JOURNAL,
    QUERY_KEYS.GET_GRADEBOOK,
    QUERY_KEYS.GET_FINISH_PREVIEW,
  ]) {
    void queryClient.invalidateQueries({ queryKey: [key, courseId] });
  }
  void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_LIVE_COURSES] });
};

export const useFinishCourse = (courseId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => liveCoursesApi.finish(courseId),
    onSuccess: () => {
      invalidateLifecycle(queryClient, courseId);
      toast.success(t("FINISH.DONE_TOAST"));
    },
  });
};

export const useReopenCourse = (courseId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => liveCoursesApi.reopen(courseId),
    onSuccess: () => {
      invalidateLifecycle(queryClient, courseId);
      toast.success(t("FINISH.REOPENED_TOAST"));
    },
  });
};
